import axios from "axios";
import config from "../config/config";
import { useUser } from "../context/UserContext";
import suburbsData from "../data/us_addresses.json";
import US_STATE_ABBR from "../data/us_states_abbr.json";

export const NO_PREFERENCE = "No Preference";
export const ALLOWED_GENDERS = ["Woman", "Man", NO_PREFERENCE];

export const useUserId = () => {
  const { workerUser, clientUser } = useUser();
  return workerUser?.id ?? clientUser?.id;
}

export const useUserRole = () => {
  const { workerUser, clientUser } = useUser();
  return workerUser?.userType ?? clientUser?.userType;
}

export const useAdminUserRole = () => {
  const { adminUser } = useUser();
  return adminUser?.[0]?.userType;
};

export const pickCorrectedText = (resp, draft) => {
  if (typeof resp?.correctedText === "string" && resp.correctedText.trim()) return resp.correctedText;

  if (typeof resp?.corrected_text === "string" && resp.corrected_text.trim()) return resp.corrected_text;

  if (typeof resp?.payload?.correctedText === "string" && resp.payload.correctedText.trim()) return resp.payload.correctedText;
  if (typeof resp?.payload?.corrected_text === "string" && resp.payload.corrected_text.trim()) return resp.payload.corrected_text;

  const jd =
    resp?.job?.jobCraftDetails?.jobDescription ||
    resp?.jobCraftDetails?.jobDescription;

  if (typeof jd === "string" && jd.trim()) return jd;

  const draftDesc = draft?.jobCraftDetails?.jobDescription;
  if (typeof draftDesc === "string" && draftDesc.trim()) return draftDesc;

  return "";
};

export const pickSummary = (resp) => {
  if (typeof resp?.formattedSummary === "string" && resp.formattedSummary.trim()) return resp.formattedSummary;
  if (typeof resp?.formatted_summary === "string" && resp.formatted_summary.trim()) return resp.formatted_summary;
  if (typeof resp?.payload?.formattedSummary === "string" && resp.payload.formattedSummary.trim()) return resp.payload.formattedSummary;
  if (typeof resp?.payload?.formatted_summary === "string" && resp.payload.formatted_summary.trim()) return resp.payload.formatted_summary;
  return "";
};


export const getClientTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

export const getClientTodayMMDDYYYY = (timeZone) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const mm = parts.find((p) => p.type === "month")?.value ?? "01";
  const dd = parts.find((p) => p.type === "day")?.value ?? "01";
  const yyyy = parts.find((p) => p.type === "year")?.value ?? "1970";

  return `${mm}/${dd}/${yyyy}`;
};



export const cleanJobDescription = (htmlString) => {
  htmlString = htmlString.replace(/<(script|style).*?>.*?<\/\1>/gi, "");

  const entitiesMap = {
    "&nbsp;": " ",
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    "&quot;": '"',
    "&#39;": "'",
  };

  htmlString = htmlString.replace(/&[a-zA-Z0-9#]+;/g, (entity) => {
    return entitiesMap[entity] || entity;
  });

  htmlString = htmlString
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim();

  return htmlString;
};

export const formatDateString = (timestamp) => {
  const date = new Date(timestamp);

  const datePart = date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const timePart = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <>
      <strong>{datePart}</strong>&nbsp;at&nbsp;<strong>{timePart}</strong>
    </>
  );
};

export const formatDateStringNoDay = (timestamp) => {
  const date = new Date(timestamp);

  const datePart = date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const timePart = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <>
      <strong>{datePart}&nbsp;at&nbsp;{timePart}</strong>
    </>
  );
};

export const formatDateStringNoTime = (timestamp) => {
  if (!timestamp || timestamp === "As Soon As Possible") {
    return <strong>As Soon As Possible</strong>;
  }

  const date = new Date(timestamp);

  const day = date.getDate();

  const getOrdinal = (n) => {
    if (n > 3 && n < 21) return "th";
    switch (n % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };

  const suffix = getOrdinal(day);

  const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear();

  return (
    <strong>
      {weekday}, {month} {day}
      <sup>{suffix}</sup>, {year}
    </strong>
  );
};

export const formatDateStringWithDayNoTime = (timestamp) => {
  const date = new Date(timestamp);

  const datePart = date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <>
      <strong>{datePart}</strong>
    </>
  );
};

export const dateStringWithDayNoTime = (timestamp) => {
  const date = new Date(timestamp);

  const datePart = date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <>
      {datePart}
    </>
  );
};


export function formatDuration(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];

  if (hours) parts.push(`${hours} hour${hours !== 1 ? "s" : ""}`);
  if (minutes) parts.push(`${minutes} minute${minutes !== 1 ? "s" : ""}`);
  if (seconds) parts.push(`${seconds} second${seconds !== 1 ? "s" : ""}`);

  return parts.length ? parts.join(", ") : "0 seconds";
}

export const formatTimestamp = (ts) => {
  if (!ts) return { date: "", time: "" };
  const dateObj = new Date(ts);

  const datePart = dateObj.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const timePart = dateObj.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return { date: datePart, time: timePart };
};

export const convertMillisToHoursAndMinutes = (millis) => {
  const hours = Math.floor(millis / (1000 * 60 * 60));
  const minutes = Math.floor((millis % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours} hours, ${minutes} minutes`;
};

export const calculateHours = (startTime, endTime) => {
  const parseTime = (timeStr) => {
    if (!timeStr) return { hours: 0, minutes: 0 };

    const [time, modifier] = timeStr.split(" ");
    let [hours, minutes] = time.split(":");
    if (hours === "12") {
      hours = "00";
    }
    if (modifier === "PM") {
      hours = parseInt(hours, 10) + 12;
    } else {
      hours = parseInt(hours, 10);
    }
    minutes = parseInt(minutes, 10);
    return { hours, minutes };
  };

  if (!startTime || !endTime) {
    return "0.00";
  }

  const start = parseTime(startTime);
  const end = parseTime(endTime);

  const startDate = new Date(0, 0, 0, start.hours, start.minutes);
  const endDate = new Date(0, 0, 0, end.hours, end.minutes);

  let diff = (endDate - startDate) / 1000 / 60 / 60;
  if (diff < 0) {
    diff += 24;
  }

  return diff.toFixed(2);
};

export const formatTimeSlots = (timeSlots) => {
  const entries = Object.entries(timeSlots);

  return (
    <ul className="time-slots-list">
      {entries.map(([day, slots]) => {
        if (slots.length > 0) {
          const slotDescriptions = slots.map((slot, index) => {
            const fromTime = slot.from;
            const toTime = slot.to;
            return (
              <li key={index}>
                {fromTime} - {toTime}
              </li>
            );
          });

          return (
            <li key={day}>
              <strong>{day}:</strong>
              <ul>{slotDescriptions}</ul>
            </li>
          );
        }
        return null;
      })}
    </ul>
  );
};

export const dateToUnixTimestamp = (date) => {
  return new Date(date).getTime();
};

export function formatUnixDate(
  ts,
  options = { year: "numeric", month: "long", day: "numeric" },
  locale = "en-US"
) {
  const date = new Date(Number(ts));
  return date.toLocaleDateString(locale, options);
}


export function formatUnixFullDate(
  ts,
  locale = "en-US",
  options = {
    weekday: "long",
    year: "numeric",
    month: "short",
    day: "numeric",
  }
) {
  return new Date(Number(ts)).toLocaleDateString(locale, options);
}

export function formatUnixShortDate(ts, locale = "en-US") {
  const date = new Date(Number(ts));
  const options = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  };
  return date.toLocaleDateString(locale, options);
}


export function formatUnixFullTime(
  ts,
  locale = "en-US",
  options = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }
) {
  return new Date(Number(ts)).toLocaleTimeString(locale, options);
}

export function formatUnixSmart(ts, locale = "en-US") {
  const date = new Date(Number(ts));
  const today = new Date();

  const isToday =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();

  if (isToday) {
    return formatUnixFullTime(ts, locale);
  } else {
    return formatUnixDate(ts, { year: "numeric", month: "short", day: "numeric" }, locale);
  }
}

// =========================
//  Avatar Color Utilities
// =========================

export const hashToIndex = (str, len) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % len;
};

// export const TILE_PALETTE = [
//   "#1F8F6B", "#2563EB", "#7C3AED", "#DB2777", "#E11D48",
//   "#CA8A04", "#059669", "#0EA5E9", "#14B8A6", "#F97316",
//   "#8B5CF6", "#6366F1", "#EF4444", "#1f8f6b", "#22C55E"
// ];

export const TILE_PALETTE = [
  "#1f538f", "#F97316", "#DB2777", "#1F8F6B", "#EF4444"
];

export const avatarColorsFor = (seed) => {
  const bg = TILE_PALETTE[hashToIndex(seed || "?", TILE_PALETTE.length)];

  const { r, g, b } = {
    r: parseInt(bg.slice(1, 3), 16),
    g: parseInt(bg.slice(3, 5), 16),
    b: parseInt(bg.slice(5, 7), 16),
  };
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  const fg = luminance > 0.6 ? "#1f2937" : "#ffffff";
  return { bg, fg };
};

export const initials = (name = "") => {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map(p => p[0]?.toUpperCase() || "").join("") || "?";
};


export const cleanPersonalDetails = (htmlString) => {
  if (!htmlString) return "";

  console.log("Original Personal Details:", htmlString);

  let cleanedHtml = htmlString
    .replace(/<p>\s*<br>\s*<\/p>/g, "")
    .replace(/<h3>\s*<br>\s*<\/h3>/g, "")
    .replace(/<p>(\s|&nbsp;)*<\/p>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .replace(/<h3>/g, '<h3 class="job-personal-details">')
    .trim();

  const parser = new DOMParser();
  const doc = parser.parseFromString(cleanedHtml, "text/html");
  cleanedHtml = doc.body.innerHTML;

  console.log("Cleaned Personal Details:", cleanedHtml);

  return cleanedHtml;
};

const parseTimeToMinutes = (t) => {
  if (!t) return NaN;
  const m = t.match(/^\s*(\d{1,2}):(\d{2})\s*(AM|PM)?\s*$/i);
  if (!m) return NaN;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const ap = m[3]?.toUpperCase();
  if (ap) {
    if (ap === "PM" && h !== 12) h += 12;
    if (ap === "AM" && h === 12) h = 0;
  }
  return h * 60 + min;
};

export const diffMinutes = (from, to) => {
  const a = parseTimeToMinutes(from);
  const b = parseTimeToMinutes(to);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.max(0, b - a);
};

export const formatMinutesAsHM = (mins) => {
  if (!Number.isFinite(mins) || mins <= 0) return "0m";

  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const parts = [];

  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);

  return parts.join(" ");
};



//   export const formatMinutesAsHM = (mins, { compact = false } = {}) => {
//   if (!Number.isFinite(mins)) return "-";
//   const h = Math.floor(mins / 60);
//   const m = mins % 60;

//   if (compact) {
//     if (h && m) return `${h}h ${m}m`;
//     if (h) return `${h}h`;
//     if (m) return `${m}m`;
//     return "0m";
//   }

//   const parts = [];
//   if (h) parts.push(`${h} hr${h === 1 ? "" : "s"}`);
//   if (m) parts.push(`${m} min${m === 1 ? "" : "s"}`);
//   return parts.join(" ") || "0 mins";
// };


export const addInlineStyles = (htmlContent) => {
  const fontStyle = "font-family: 'Quicksand', sans-serif;";

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");

  const elements = doc.body.querySelectorAll("*");
  elements.forEach((element) => {
    element.setAttribute("style", fontStyle);
  });

  const updatedHtmlContent = doc.body.innerHTML;

  return updatedHtmlContent;
};

export const formatBadgeType = (badgeType) => {
  return badgeType
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .trim()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const formatBadgeValue = (badgeValue) => {
  return badgeValue.charAt(0).toUpperCase() + badgeValue.slice(1);
};

export const getImageDetailsForBadge = (badgeType) => {
  const imageDetails = {
    petFriendly: {
      src: "/images/pet-friendly.jpg",
      alt: "Pet Friendly",
    },
    nonSmoker: {
      src: "/images/non-smoker.jpg",
      alt: "Non Smoker",
    },
  };

  return (
    imageDetails[badgeType] || {
      src: "/images/icon-image-lgbti.svg",
      alt: "LGBTQIA+ Community",
    }
  );
};

export const formatText = (text) => {
  if (text.toLowerCase() === "yes" || text.toLowerCase() === "no") {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  return text
    .replace(/([A-Z])/g, " $1")
    .trim()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const getStepFromPath = () => {
  const pathParts = window.location.pathname.split("/");
  const stepSegment = pathParts.find((part) => part.startsWith("step-"));
  return stepSegment ? parseInt(stepSegment.replace("step-", ""), 10) : 1;
};

export const clearSearchSelectionsHelper = async (selectionId) => {
  try {
    if (!selectionId) {
      console.warn("Selection ID is missing. Cannot clear search selections.");
      return;
    }

    console.log(`Attempting to clear search selections for ID: ${selectionId}`);

    await axios.delete(
      `${config.API_BASE_URL}/v1/worker/search-selections/${selectionId}`
    );

    document.cookie =
      "searchSelectionId=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";

    console.log("Search selections cleared successfully.");
  } catch (error) {
    console.error("Error clearing search selections:", error);
  }
};

export const clearJobCreateIdHelper = async (jobCreateId) => {
  try {
    if (!jobCreateId) {
      console.warn("JobCreateId is missing. Cannot clear job selections.");
      return;
    }

    console.log(
      `Attempting to clear job selections for jobCreateId: ${jobCreateId}`
    );

    await axios.delete(`${config.API_BASE_URL}/v1/job/create/${jobCreateId}`);

    document.cookie =
      "jobCreateId=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";

    console.log("Job selections cleared successfully.");
  } catch (error) {
    console.error("Error clearing job selections:", error);
  }
};

export function validateSupportJobLocationDetails(locationDetails) {
  const errors = [];
  const { supportJobLocation, preferredGender } = locationDetails;

  if (!supportJobLocation || !supportJobLocation.trim()) {
    errors.push({ field: "supportJobLocation", message: "Support Job location is required." });
  }

  const gender = ((preferredGender ?? NO_PREFERENCE) + "").trim();
  if (!ALLOWED_GENDERS.includes(gender)) {
    errors.push({ field: "preferredGender", message: "Please select a valid gender option." });
  }

  return errors;
}

export const validateFrequencyDetails = (freq) => {
  const errors = [];
  if (!freq) return errors;

  const toNum = (v) =>
    v === "" || v == null ? null : (Number.isFinite(Number(v)) ? Number(v) : null);
  const lower = (s) => String(s || "").trim().toLowerCase();

  const PAY_LIMITS = {
    hourly: { min: 0, max: 60 },
    daily: { min: 0, max: 1500 },
    annual: { min: 0, max: 250000 },
  };

  const jobType = String(freq.jobType || "").trim();
  const supportType = String(freq.supportType || "").trim();
  const scheduling = lower(freq.scheduling);
  const sessions = toNum(freq.sessionsPerWeek);
  const hours = toNum(freq.hoursPerSession);

  const preferredDays = freq.preferredDays || {};
  const timeSlots = freq.timeSlots || {};

  if (!jobType) {
    errors.push({ field: "jobType", message: "Job type selection is required." });
  }
  if (!supportType) {
    errors.push({ field: "supportType", message: "Session type selection is required." });
  }
  if (!scheduling) {
    errors.push({ field: "scheduling", message: "Please select a scheduling preference." });
  }

  const unit = ["hourly", "daily", "annual"].includes(freq.payType) ? freq.payType : "";
  if (!unit) {
    errors.push({ field: "pay", message: "Select a pay type." });
  } else {
    const lim = PAY_LIMITS[unit];

    const fixed =
      unit === "hourly" ? toNum(freq.payRateHourly) :
        unit === "daily" ? toNum(freq.payRateDaily) :
          toNum(freq.payRateAnnual);

    const rng = (freq.payRanges && freq.payRanges[unit]) || {};
    const rMin = toNum(rng.min);
    const rMax = toNum(rng.max);
    const hasRange = rMin != null || rMax != null;

    if (fixed == null && !hasRange) {
      errors.push({ field: "pay", message: `Provide a ${unit} rate or a ${unit} range.` });
    } else {
      if (fixed != null) {
        if (fixed <= 0) errors.push({ field: "pay", message: `The ${unit} rate must be greater than 0.` });
        if (fixed < lim.min) errors.push({ field: "pay", message: `The ${unit} rate cannot be below ${lim.min}.` });
        if (fixed > lim.max) errors.push({ field: "pay", message: `The ${unit} rate cannot exceed ${lim.max}.` });
      }
      if (hasRange) {
        if (rMin != null) {
          if (rMin <= 0) errors.push({ field: "pay", message: `The ${unit} minimum must exceed 0.` });
          else if (rMin < lim.min) errors.push({ field: "pay", message: `The ${unit} minimum cannot be below ${lim.min}.` });
        }
        if (rMax != null) {
          if (rMax <= 0) errors.push({ field: "pay", message: `The ${unit} maximum must be greater than 0.` });
          else if (rMax > lim.max) errors.push({ field: "pay", message: `The ${unit} maximum cannot exceed ${lim.max}.` });
        }
        if (rMin != null && rMax != null && rMin > rMax) {
          errors.push({ field: "pay", message: `The ${unit} minimum cannot be greater than the maximum.` });
        }
      }
    }
  }

  if (scheduling === "preferred") {
    const hasAnyDayOn = Object.values(preferredDays).some(Boolean);
    if (!hasAnyDayOn) {
      errors.push({
        field: "preferredDays",
        message: "Please select at least one preferred day.",
      });
    }

    let validSlotCount = 0;
    Object.entries(preferredDays).forEach(([day, on]) => {
      if (!on) return;
      const slots = Array.isArray(timeSlots[day]) ? timeSlots[day] : [];
      slots.forEach((slot, idx) => {
        const fromMin = parseTime(slot?.from);
        const toMin = parseTime(slot?.to);
        if (!Number.isFinite(fromMin) || !Number.isFinite(toMin) || fromMin >= toMin) {
          errors.push({
            field: `timeSlot-${day}-${idx}`,
            message: `Start time (${slot?.from ?? "-"}) must be earlier than end time (${slot?.to ?? "-"}).`,
          });
        } else {
          validSlotCount += 1;
        }
      });
    });

    if (hasAnyDayOn && validSlotCount === 0) {
      errors.push({
        field: "preferredDays",
        message: "Add at least one valid time slot.",
      });
    }


  } else if (scheduling === "flexible") {
    if (supportType === "Ongoing") {
      if (!Number.isInteger(sessions) || sessions < 1) {
        errors.push({
          field: "sessionsPerWeek",
          message: "Please enter sessions per week.",
        });
      }
      if (!(hours > 0)) {
        errors.push({
          field: "hoursPerSession",
          message: "Please enter hours per session.",
        });
      }
    } else if (supportType === "One-Off") {
      if (!(hours > 0)) {
        errors.push({
          field: "hoursPerSession",
          message: "Please enter the expected time block (hours).",
        });
      }
    }
  }

  if (!freq.supportStartDate) {
    errors.push({
      field: "supportStartDate",
      message: "Preferred start date is required.",
    });
  }

  return errors;
};


const formatMoneyNoCents = (n, currency = "USD") => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "-";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(num);
  } catch {
    return `${Math.round(num)} ${currency}`;
  }
};

const unitSuffix = (payType) =>
  payType === "hourly" ? "/ hour" :
    payType === "daily" ? "/ day" :
      payType === "annual" ? "/ yr" : "";

const currencySymbol = (c = "USD") => ({
  USD: "$",
  EUR: "€",
  GBP: "£",
  AUD: "A$",
  CAD: "C$",
  NZD: "NZ$",
})[c] || `${c} `;

const formatMoneyCompact = (value, currency = "USD") => {
  if (value == null || value === "") return "";
  const n = Number(value);
  const sym = currencySymbol(currency);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (abs >= 1_000) return `${sym}${Math.round(n / 1000)}k`;
  return `${sym}${Math.round(n)}`;
};

const unitSuffixAbbr = (pt) =>
  pt === "hourly" ? "/ hour" : pt === "daily" ? "/ day" : "/ yr";

export const buildCompString = (fd, opts = {}) => {
  const { compact = false } = opts;

  if (!fd) return "-";
  const pt = ["hourly", "daily", "annual"].includes(fd.payType) ? fd.payType : "";
  if (!pt) return "-";

  const c = fd.currency || "USD";

  const fixed =
    pt === "hourly" ? fd.payRateHourly :
      pt === "daily" ? fd.payRateDaily :
        fd.payRateAnnual;

  if (fixed != null) {
    if (compact) {
      return `${formatMoneyCompact(fixed, c)} ${unitSuffixAbbr(pt)}`;
    }
    return `${formatMoneyNoCents(fixed, c)} ${unitSuffix(pt)}`;
  }

  const rng = fd.payRanges?.[pt] || {};
  const { min, max } = rng;

  if (min != null && max != null) {
    if (Number(min) === Number(max)) {
      return compact
        ? `${formatMoneyCompact(min, c)} ${unitSuffixAbbr(pt)}`
        : `${formatMoneyNoCents(min, c)} ${unitSuffix(pt)}`;
    }
    return compact
      ? `${formatMoneyCompact(min, c)} – ${formatMoneyCompact(max, c)} ${unitSuffixAbbr(pt)}`
      : `${formatMoneyNoCents(min, c)} – ${formatMoneyNoCents(max, c)} ${unitSuffix(pt)}`;
  }

  if (min != null && max == null) {
    return compact
      ? `${formatMoneyCompact(min, c)} ${unitSuffixAbbr(pt)}`
      : `${formatMoneyNoCents(min, c)} ${unitSuffix(pt)}`;
  }

  if (max != null && min == null) {
    return compact
      ? `${formatMoneyCompact(max, c)} ${unitSuffixAbbr(pt)}`
      : `${formatMoneyNoCents(max, c)} ${unitSuffix(pt)}`;
  }

  return "-";
};




export const parseTime = (timeStr) => {
  if (!timeStr) return 0;

  const [timePart, ampm] = timeStr.split(" ");
  let [hour, minute] = timePart.split(":").map(Number);

  if (hour === 12) {
    hour = 0;
  }

  if (ampm === "PM") {
    hour += 12;
  }

  return hour * 60 + minute;
};



export function timeAgo(postedTime) {
  const now = new Date();
  const then = new Date(postedTime);
  const diffInSeconds = Math.floor((now - then) / 1000);

  const lbl = (n, singular, plural) => {
    return `${n} ${n === 1 ? singular : plural}`;
  };

  if (diffInSeconds < 60) {
    return "seconds";
  }

  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return lbl(minutes, "minute", "minutes");
  }

  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return lbl(hours, "hour", "hours");
  }

  const days = Math.floor(diffInSeconds / 86400);
  return lbl(days, "day", "days");
}


export const truncateJobTitle = (str, maxLength) => {
  if (str.length > maxLength) {
    return str.substring(0, maxLength) + "...";
  } else {
    return str;
  }
};

export const isEmptyValue = (value) =>
  !value || value.trim() === "" || value.trim() === "<p><br></p>";

export const validateJobCraftDetails = (details) => {
  const errors = [];

  if (isEmptyValue(details.jobTitle)) {
    errors.push({
      field: "jobTitle",
      message: "Job title is required.",
    });
    console.log("Validation: jobTitle is missing.");
  } else {
    console.log("Validation: jobTitle is present:", details.jobTitle);
  }

  if (isEmptyValue(details.jobDescription)) {
    errors.push({
      field: "jobDescription",
      message: "Job description is required.",
    });
    console.log("Validation: jobDescription is missing.");
  } else {
    console.log(
      "Validation: jobDescription is present:",
      details.jobDescription
    );
  }

  if (isEmptyValue(details.personalDetails)) {
    errors.push({
      field: "personalDetails",
      message: "Additional details are required.",
    });
    console.log("Validation: personalDetails is missing.");
  } else {
    console.log(
      "Validation: personalDetails is present:",
      details.personalDetails
    );
  }

  console.log("Validation errors:", errors);
  return errors;
};

export const customSelectStyles = (isMobile = false) => ({
  height: '40px',
  lineHeight: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #1a6b5d',
  borderRadius: isMobile ? '8px' : '4px',
  cursor: 'pointer'
});

export const customSelectStyles2 = (isMobile = false) => ({
  height: '30px',
  lineHeight: '30px',
  fontWeight: '600',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #d8d8d8ff',
  borderRadius: isMobile ? '8px' : '6px',
  cursor: 'pointer'
});

export const centeredContentRenderer = ({ props, state }) => (
  <div
    style={{
      width: '100%',
      textAlign: 'center',
      fontSize: '0.9rem',
    }}
  >
    {state.values.length === 0
      ? props.placeholder
      : state.values.map((v) => v.label).join(', ')}
  </div>
);

export const customItemRenderer = ({ item, state, methods }) => {
  const isSelected = state.values.some((val) => val.value === item.value);

  return (
    <div
      onClick={() => methods.addItem(item)}
      style={{
        padding: '8px 15px',
        textAlign: 'left',
        backgroundColor: isSelected ? '#E8FFF2' : 'white',
        fontWeight: isSelected ? '600' : '500',
        border: '1px solid #F1F1F1',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = '#f0f0f0';
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = 'white';
        }
      }}
    >
      {item.label}
    </div>
  );
};

export const alignedContentRenderer = ({ props, state }) => (
  <div
    style={{
      width: '100%',
      textAlign: 'left',
      fontSize: '0.95rem',
      paddingLeft: '8px',
    }}
  >
    {state.values.length === 0
      ? props.placeholder
      : state.values.map((v) => v.label).join(', ')}
  </div>
);

export const paddedItemRenderer = ({ item, state, methods }) => {
  const isSelected = state.values.some((val) => val.value === item.value);

  return (
    <div
      onClick={() => methods.addItem(item)}
      style={{
        padding: '10px 12px',
        backgroundColor: isSelected ? '#eefcf8' : '#fff',
        fontWeight: isSelected ? '500' : 'normal',
        borderBottom: '1px solid #e0e0e0',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
      }}
      onMouseEnter={(e) =>
      (e.currentTarget.style.backgroundColor = isSelected
        ? '#d8f5ee'
        : '#f7f7f7')
      }
      onMouseLeave={(e) =>
      (e.currentTarget.style.backgroundColor = isSelected
        ? '#eefcf8'
        : '#fff')
      }
    >
      {item.label}
    </div>
  );
};

export const paddedItemRenderer2 = ({ item, state, methods }) => {
  const isSelected = state.values.some((val) => val.value === item.value);

  return (
    <div
      onClick={() => methods.addItem(item)}
      style={{
        fontSize: '0.9rem',
        textAlign: 'center',
        padding: '8px 5px',
        backgroundColor: isSelected ? '#eefcf8' : '#fff',
        fontWeight: isSelected ? '500' : 'normal',
        borderBottom: '1px solid #e0e0e0',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
      }}
      onMouseEnter={(e) =>
      (e.currentTarget.style.backgroundColor = isSelected
        ? '#d8f5ee'
        : '#f7f7f7')
      }
      onMouseLeave={(e) =>
      (e.currentTarget.style.backgroundColor = isSelected
        ? '#eefcf8'
        : '#fff')
      }
    >
      {item.label}
    </div>
  );
};


export const customDropdownStyles = (isMobile = false) => ({
  fontSize: '0.9rem',
  height: '40px',
  lineHeight: '40px',
  backgroundColor: '#f1f1f1',
  border: '1px solid #dae4e1',
  borderRadius: '5px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  width: 'auto',
});

export const jobDropdownStyles = (isMobile = false) => ({
  fontSize: '0.9rem',
  fontWeight: '500',
  lineHeight: '2rem',
  backgroundColor: 'white',
  border: '1px solid #ebebeb',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  width: 'auto',
  boxShadow: "0 1px 4px rgba(121, 148, 148, 0.22)",
});

export const sortDropdownItemRenderer = ({ item, state, methods }) => {
  const isSelected = state.values.some((val) => val.value === item.value);

  return (
    <div
      onClick={() => methods.addItem(item)}
      style={{
        padding: '3px',
        backgroundColor: isSelected ? '#d0f0ea' : 'white',
        fontWeight: isSelected ? '600' : 'normal',
        border: '1px solid rgb(243, 243, 243)',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
      }}
      onMouseEnter={(e) =>
      (e.currentTarget.style.backgroundColor = isSelected
        ? '#c2e7dd'
        : '#f0f0f0')
      }
      onMouseLeave={(e) =>
      (e.currentTarget.style.backgroundColor = isSelected
        ? '#d0f0ea'
        : 'white')
      }
    >
      {item.label}
    </div>
  );
};

export const chatFilterDropdownItemRenderer = ({ item, state, methods }) => {
  const isSelected = state.values.some((val) => val.value === item.value);

  return (
    <div
      onClick={() => methods.addItem(item)}
      style={{
        padding: '3px',
        backgroundColor: isSelected ? '#d0f0ea' : 'white',
        fontWeight: isSelected ? '600' : 'normal',
        border: '1px solid rgb(243, 243, 243)',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
      }}
      onMouseEnter={(e) =>
      (e.currentTarget.style.backgroundColor = isSelected
        ? '#c2e7dd'
        : '#f0f0f0')
      }
      onMouseLeave={(e) =>
      (e.currentTarget.style.backgroundColor = isSelected
        ? '#d0f0ea'
        : 'white')
      }
    >
      {item.label}
    </div>
  );
};

export const dropdownContentRenderer = ({ props, state }) => (
  <div style={{
    textAlign: 'left',
    padding: '0 8px',
    fontSize: '0.9rem',

  }}>
    {state.values.length === 0
      ? props.placeholder
      : state.values.map(v => v.label).join(', ')}
  </div>
);

export const noBorderItemRenderer = ({ item, state, methods }) => {
  const isSelected = state.values.some(v => v.value === item.value);
  return (
    <div
      onClick={() => methods.addItem(item)}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '4px',
        minHeight: '30px',
        backgroundColor: isSelected ? '#e6f4f1' : '#fff',
        fontWeight: isSelected ? 500 : 'normal',
        border: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.backgroundColor = isSelected
          ? '#d0ebe6'
          : '#f7f7f7';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.backgroundColor = isSelected
          ? '#e6f4f1'
          : '#fff';
      }}
    >
      {item.label}
    </div>
  );
};


export function sortAndFilter(threads = []) {
  return threads
    .filter(t => Array.isArray(t.fullMessages) && t.fullMessages.length > 0)
    .sort((a, b) => {
      const aMax = Math.max(...a.fullMessages.map(m => new Date(m.timestamp)));
      const bMax = Math.max(...b.fullMessages.map(m => new Date(m.timestamp)));
      return bMax - aMax;
    });
}

export function upsertMessage(threads = [], incoming) {
  const tid = incoming.threadId ?? incoming.id;
  let found = false;
  const updated = threads.map(t => {
    if (t.id === tid) {
      found = true;
      return {
        ...t,
        fullMessages: [...t.fullMessages, incoming],
        latestMessage: incoming.text,
        timestamp: incoming.timestamp
      };
    }
    return t;
  });
  if (!found) {
    updated.unshift({
      id: tid,
      client: incoming.clientName || "",
      fullMessages: [incoming],
      latestMessage: incoming.text,
      timestamp: incoming.timestamp
    });
  }
  return updated;
}

export function computeUnreadCounts(threads, currentUserRole) {
  const counts = threads.reduce((acc, thread) => {
    const c = (thread.fullMessages || [])
      .filter(
        m => m.receiver === currentUserRole
          && m.read === false
      ).length;
    if (c > 0) acc[thread.id] = c;
    return acc;
  }, {});

  console.log("[computeUnreadCounts] for role", currentUserRole, counts);
  return counts;
}


export function fetchAvatars({ clientId, workerId, defaultAvatar, imgUrl }) {
  const clientImageUrl = clientId
    ? `${imgUrl}/${clientId}.jpg`
    : defaultAvatar;

  const workerImageUrl = workerId
    ? `${imgUrl}/${workerId}.jpg`
    : defaultAvatar;

  return { clientImageUrl, workerImageUrl };
}

let _notifAudio = null;

export function playNotificationSound() {
  const audio = new Audio("/audio/ping.mp3");
  return audio.play();
}

export async function handleUseCurrentLocation({
  setSuburbValue,
  setAddressValidationMessage,
  setValidationMessageClass,
  setIsLocating,
}) {
  if (!navigator.geolocation) {
    setAddressValidationMessage("Geolocation not supported in your browser.");
    setValidationMessageClass("address-invalid");
    return;
  }

  setIsLocating(true);

  try {
    const position = await new Promise((resolve, reject) =>
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 60000,
      })
    );

    const { latitude, longitude } = position.coords;
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`
    );
    const data = await res.json();

    const city = data.address.city || data.address.town || data.address.village;
    const rawState = data.address.state_code || data.address.state || "";
    const stateAbbr =
      rawState.length === 2
        ? rawState.toUpperCase()
        : US_STATE_ABBR[rawState.replace(/ /g, "_")] || rawState;
    const zip = data.address.postcode || "";

    if (!city || !stateAbbr || !zip) {
      throw new Error("Incomplete address data");
    }

    setSuburbValue(`${city} ${stateAbbr} ${zip}`);
  } catch (err) {
    console.error(err);
    const msg =
      err.message === "Incomplete address data"
        ? "Could not resolve your address. Please enter manually."
        : "Unable to retrieve your location. Please allow location access or enter manually.";
    setAddressValidationMessage(msg);
    setValidationMessageClass("address-invalid");
  } finally {
    setIsLocating(false);
  }
}

export function handleSuburbChange(e, setSuburbValue, setSuggestions) {
  const inputValue = e.target.value;
  setSuburbValue(inputValue);

  const q = inputValue.trim().toLowerCase();
  const MAX_RESULTS = 5;

  if (!q) {
    setSuggestions([]);
    return;
  }

  const filtered = suburbsData.Suburbs_Zipcodes
    .filter((addr) =>
      addr.Post_City.toLowerCase().startsWith(q) ||
      addr.State.toLowerCase().startsWith(q) ||
      addr.Zip_Code.toString().startsWith(q)
    )
    .slice(0, MAX_RESULTS);

  setSuggestions(filtered);
}

export function handleSuggestionClick(
  selectedSuburb,
  setSuburbValue,
  setSuggestions
) {
  setSuburbValue(selectedSuburb);
  setSuggestions([]);
}

export function handleClearSuburb(
  setSuburbValue,
  setSuggestions,
  setAddressValidationMessage,
  inputRef
) {
  setSuburbValue("");
  setSuggestions([]);
  setAddressValidationMessage("");
  if (inputRef.current) {
    inputRef.current.focus();
  }
}






